/**
 * Bag Beat (owner, Sep 28: "rhythm punching") — a phone game (`game:bag_beat`): the gym's heavy bag to a beat. Gloves come down
 * three lanes to the ring; punch the lane as a glove reaches it (← ↓ →, A S D or J K L; a tap on the lane). Right on the beat is
 * 100, near it 50, times the combo's multiplier; a glove missed or a punch at nothing wears your guard down, and an empty guard is
 * a knock-out. The tempo climbs every sixteen beats.
 */
import { clamp, core, end, pop, rnd, tick, D, L, R, U, type Core, type MiniGame } from './core';
import { box, disc, halftone, hud, pops, shout } from './draw';

const W = 180, H = 320, HIT_Y = 262, TOP_Y = 128, LANE_X = [42, 90, 138];
/** seconds a glove takes from the top of its lane to the ring */
export const LEAD = 1.4;
/** the timing windows (seconds either side of the beat) */
export const PERFECT = 0.06, GOOD = 0.14;
const START = 1.2;
export interface Note { at: number; lane: number; done: 0 | 1 | 2 }
export interface Bag extends Core { notes: Note[]; beats: number[]; genAt: number; genN: number; combo: number; best: number; guard: number; swing: number; sv: number; hits: number }
export const bpmAt = (n: number): number => Math.min(150, 96 + Math.floor(n / 16) * 6);
const WORDS = ['POW!', 'BAM!', 'WHAM!', 'THUD!'];

/** punch a lane: the nearest glove in the window lands, or it is a punch at nothing */
function punch(s: Bag, lane: number): void {
  let best: Note | undefined; for (const n of s.notes) if (!n.done && n.lane === lane && Math.abs(n.at - s.t) < GOOD && (!best || Math.abs(n.at - s.t) < Math.abs(best.at - s.t))) best = n;
  if (!best) { s.combo = 0; s.guard -= 0.05; s.fx.push('miss'); if (s.guard <= 0) ko(s); return; }
  best.done = 1; s.hits++;
  const perfect = Math.abs(best.at - s.t) < PERFECT, mult = 1 + Math.min(3, Math.floor(s.combo / 10));
  s.score += (perfect ? 100 : 50) * mult; s.combo++; s.best = Math.max(s.best, s.combo); s.guard = Math.min(1, s.guard + (perfect ? 0.03 : 0.01));
  s.sv += (lane - 1 || (rnd(s) < 0.5 ? -1 : 1)) * (perfect ? 2.6 : 1.6); s.fx.push('punch');
  pop(s, LANE_X[lane], 96, perfect ? WORDS[Math.floor(rnd(s) * WORDS.length)] : 'OK!', perfect ? '#ffe14a' : '#9ad0ff');
}
function ko(s: Bag): void { s.guard = 0; s.shake = 6; pop(s, W / 2, 180, 'K.O.!', '#ff3b30'); end(s); }

export const bag: MiniGame<Bag> = {
  id: 'bag_beat', name: 'Bag Beat', icon: '🥊', color: '#a01a2a', where: 'phone', unlock: 'game:bag_beat',
  blurb: 'Punch the heavy bag on the beat. Keep your guard up.',
  keys: '← ↓ →  (A S D, J K L) punch the lanes', touch: 'tap the lane as the glove reaches the ring',
  w: W, h: H, maxRate: 1400, maxScore: 5000000,
  init(seed) { const s = core(seed) as Bag; Object.assign(s, { notes: [], beats: [], genAt: START, genN: 0, combo: 0, best: 0, guard: 1, swing: 0, sv: 0, hits: 0 }); return s; },
  step(s, inp, dt) {
    tick(s, dt); if (s.over) return;
    // the beat ahead: a glove on most beats, now and then one on the off-beat too, a lane at random (rarely the same three times)
    while (s.genAt < s.t + LEAD + 0.2) {
      const beat = 60 / bpmAt(s.genN);
      s.beats.push(s.genAt);
      if (s.genN >= 4 && rnd(s) < 0.78) { const last = s.notes.slice(-2).map((n) => n.lane); let lane = Math.floor(rnd(s) * 3); if (last.length === 2 && last[0] === lane && last[1] === lane) lane = (lane + 1) % 3; s.notes.push({ at: s.genAt, lane, done: 0 }); }
      if (s.genN >= 16 && rnd(s) < 0.22 + Math.min(0.2, s.genN / 400)) s.notes.push({ at: s.genAt + beat / 2, lane: Math.floor(rnd(s) * 3), done: 0 });
      s.genAt += beat; s.genN++;
    }
    while (s.beats.length && s.beats[0] <= s.t) { s.beats.shift(); s.fx.push('beat'); }
    // the punches: a key's lane, or the lane a tap lands in
    if (inp.press & L) punch(s, 0);
    if (inp.press & (D | U)) punch(s, 1);
    if (inp.press & R) punch(s, 2);
    if (inp.ptap && inp.px >= 0 && inp.py > TOP_Y - 20) punch(s, inp.px < 66 ? 0 : inp.px < 114 ? 1 : 2);
    if (s.over) return;
    for (const n of s.notes) if (!n.done && s.t - n.at > GOOD) { n.done = 2; s.combo = 0; s.guard -= 0.12; s.fx.push('miss'); pop(s, LANE_X[n.lane], HIT_Y - 22, 'MISS', '#8a97a8'); if (s.guard <= 0) { ko(s); return; } }
    s.notes = s.notes.filter((n) => s.t - n.at < 0.5);
    // the bag on its chain: a spring the punches knock
    s.sv += -s.swing * 40 * dt; s.sv *= Math.pow(0.1, dt); s.swing = clamp(s.swing + s.sv * dt, -0.6, 0.6);
  },
  draw(ctx, s) {
    const sh = s.shake ? Math.sin(s.t * 80) * s.shake : 0; ctx.save(); ctx.translate(sh, 0);
    ctx.fillStyle = '#5a1e22'; ctx.fillRect(-6, 0, W + 12, H); halftone(ctx, 0, 0, W, H, 'rgba(0,0,0,0.22)', 6, 1.3);
    // the beat: the whole gym pulses on it
    { const per = 60 / bpmAt(s.genN), since = s.beats.length ? per - (s.beats[0] - s.t) : 1, pulse = clamp(1 - since / 0.15, 0, 1); if (s.t > START) { ctx.fillStyle = `rgba(255,200,80,${pulse * 0.14})`; ctx.fillRect(0, 0, W, H); } }
    // the bag
    ctx.save(); ctx.translate(W / 2, 20); ctx.rotate(s.swing * 0.5);
    ctx.strokeStyle = '#aaa'; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(0, 14); ctx.stroke();
    box(ctx, -18, 14, 36, 84, '#c8322a', 12, 2.5); ctx.fillStyle = 'rgba(255,255,255,0.25)'; ctx.fillRect(-12, 20, 5, 70); box(ctx, -19, 36, 38, 5, '#222', 2, 1.2); box(ctx, -19, 74, 38, 5, '#222', 2, 1.2);
    ctx.restore();
    // the three lanes, the rings, the gloves coming down
    for (let i = 0; i < 3; i++) { const x = LANE_X[i]; box(ctx, x - 20, TOP_Y - 8, 40, HIT_Y - TOP_Y + 30, 'rgba(0,0,0,0.35)', 8, 1.2); disc(ctx, x, HIT_Y, 17, ['#3a7aff', '#f2c230', '#ff4a3a'][i], 2.5); disc(ctx, x, HIT_Y, 11, '#2a1418', 1.5); shout(ctx, ['←', '↓', '→'][i], x, HIT_Y + 1, 11, '#fff'); }
    for (const n of s.notes) {
      if (n.done === 1) continue;
      const k = 1 - (n.at - s.t) / LEAD; if (k < 0) continue;
      const y = TOP_Y + (HIT_Y - TOP_Y) * k, x = LANE_X[n.lane];
      ctx.globalAlpha = n.done === 2 ? 0.35 : 1;
      box(ctx, x - 12, y - 10, 24, 20, '#e8413a', 9, 2); box(ctx, x - 13, y + 4, 8, 8, '#e8413a', 4, 1.5); ctx.fillStyle = 'rgba(255,255,255,0.45)'; ctx.fillRect(x - 6, y - 7, 10, 3);
      ctx.globalAlpha = 1;
    }
    ctx.restore();
    // the guard, the combo
    box(ctx, 10, H - 14, W - 20, 8, '#2a1418', 4, 1.5); box(ctx, 10, H - 14, (W - 20) * clamp(s.guard, 0, 1), 8, s.guard > 0.35 ? '#5ad06a' : '#ff4a3a', 4, 1.5);
    shout(ctx, 'GUARD', W / 2, H - 10, 7, '#fff');
    if (s.combo >= 3) shout(ctx, `${s.combo} COMBO${s.combo >= 10 ? ` ×${1 + Math.min(3, Math.floor(s.combo / 10))}` : ''}`, W / 2, TOP_Y - 18, 11, '#ffe14a');
    if (s.t < START + 0.6) shout(ctx, s.t < START ? 'READY…' : 'FIGHT!', W / 2, 190, 22, '#ffe14a');
    pops(ctx, s);
    hud(ctx, s, W, `${bpmAt(s.genN)} BPM`);
  },
};
